export const WEAPON = {
  id: 'ak',
  label: 'AKM',
  class: 'carbine',
  caliber: '7.62x39',
  /**
   * THE FIRST PURCHASABLE CARBINE. Until this weapon the carbine class had
   * exactly one entry — the M4A1 in defs/rifle.js, which is the free starter
   * at price 0 — so every gun a player could actually buy was an SMG, pistol,
   * shotgun, sniper, DMR, LMG or launcher. 1100 sits between the shotgun/
   * revolver pair at 1200 and the SMG at 800: a step up from the starter
   * carbine in raw per-shot damage and wall penetration, bought with rate of
   * fire, recoil and effective range, and cheap enough to be the first thing
   * most players buy rather than a late-game unlock.
   */
  price: 1100,
  /* --- fire control ---
   * 600 rpm is the AKM's real cyclic rate (the AK-47 before it ran ~640), a
   * quarter slower than the M4A1's 800. There is no burst position on a
   * Kalashnikov selector — it is a three-position lever reading safe / full /
   * semi from the top down — so `modes` has two entries and the burst fields
   * below are neutral placeholders never reached, the same pattern
   * defs/sniper.js and defs/lmg.js use. models/ak.js models that exact lever.
   */
  rpm: 600,
  modes: ['auto', 'semi'],
  burstCount: 1,
  burstRpm: 600,
  burstDelay: 0.1,
  /* --- ammunition ---
   * 30 rounds, the standard steel/polymer AKM magazine. Reserve is 6 spare
   * magazines against the carbine's 7: the same load-out weight buys fewer
   * 7.62x39 rounds than 5.56, which is the real trade a rifleman makes
   * between the two cartridges.
   */
  magSize: 30,
  reserve: 180,
  /* --- terminal ballistics ---
   * Real 7.62x39 M43 ball out of a 415 mm barrel: ~715 m/s, against the
   * 5.56x45's ~880 m/s from the M4's 368 mm. The heavier, blunter bullet hits
   * harder on contact (damage 40 vs 33) and pushes through cover better
   * (penetration 1.15 vs 1.0), and loses that advantage with distance — a far
   * worse ballistic coefficient means more drag (0.34 vs 0.28), a shorter
   * useful range (380 m vs 420) and more damage bled off at the far end of it
   * (dropoff 0.55 vs 0.62, i.e. 55% of full damage at maxRange rather than
   * 62%). Sustained DPS lands at 400/s against the carbine's 440/s, so the
   * trade is honest in both directions.
   */
  muzzleVelocity: 715,
  damage: 40,
  penetration: 1.15,
  dropoff: 0.55,
  maxRange: 380,
  dragK: 0.34,
  tracerEvery: 3,
  /* --- accuracy (degrees) ---
   * Looser than the M4A1 everywhere: more mass moving per cycle, a longer
   * stroke, open sights or a side-mounted reflex instead of a tube on a
   * free-float rail. spreadPerShot is up a third (0.38 vs 0.3) and decays
   * slower (3.4 vs 3.6), which is what makes a long burst from this weapon
   * genuinely worse than a long burst from the starter carbine.
   */
  spreadHip: 2.35,
  spreadAds: 0.3,
  spreadPerShot: 0.38,
  spreadMax: 3.9,
  spreadDecay: 3.4,
  /* --- recoil ---
   * The Kalashnikov's signature: a long-stroke piston slamming a heavy
   * carrier back and forward inside the receiver on every shot, so the climb
   * is bigger than the carbine's (pitch 0.0122 vs 0.0085) and — the part
   * players actually feel — it WANDERS. `drift` 0.82 against the carbine's
   * 0.55 and `yaw` 0.0036 against 0.0022 make the pattern pull sideways much
   * harder, which is the real, learnable difference between the two guns;
   * `patternSeed` keeps it deterministic so it stays learnable.
   */
  recoil: {
    pitch: 0.0122,
    yaw: 0.0036,
    kickBack: 0.024,
    kickUp: 0.0092,
    roll: 0.042,
    punch: 0.46,
    freq: 7.6,
    damping: 0.44,
    patternLength: 30,
    patternSeed: 0x7a62c3,
    climbShape: [1.5, 1.34, 1.18, 1.07, 1.0],
    drift: 0.82,
  },
  /* --- handling (seconds) ---
   * Slower than the carbine across the board and slowest on the empty
   * reload: an AK magazine is ROCKED in off a front lug rather than dropped
   * into a well, so a reload is a two-stage motion and the empty one has to
   * strip the spent magazine forward first (reloadEmpty 3.3 vs the M4's 2.9).
   */
  adsTime: 0.26,
  adsFov: 0.78,
  viewFov: 0.86,
  reloadTac: 2.4,
  reloadEmpty: 3.3,
  inspectTime: 3.4,
  drawTime: 0.68,
  holsterTime: 0.44,
  /* --- pose ---
   * SOLVED FROM THE BORE AXIS, by the method in defs/rifle.js's derivation
   * comment rather than by copying its numbers. Weapon-local geometry, taken
   * off models/ak.js's own build rather than assumed: muzzle crown
   * (0, 0.072, -0.547), front sight post (0, 0.096, -0.455), reflex window
   * (0, 0.132, +0.053), magwell mouth (0, 0.060, -0.072), magazine floorplate
   * (0, -0.129, -0.158) once the magazine is rocked into its 0.16 rad seat,
   * support wrist (-0.1, 0.067, -0.235).
   *
   * Constraints, in the same order rifle.js applies them:
   *   1. bore axis 4.6 deg LEFT of view-forward and 2.9 deg nose-down, so it
   *      converges on the crosshair: rx = -0.050, ry = +0.080.
   *   2. rolled 7.7 deg so the LEFT flank — the side carrying the rollmark,
   *      and on this weapon also the side-rail optic mount — faces the
   *      camera: rz = -0.135. rifle.js, smg.js, sniper.js and lmg.js all land
   *      within a couple of hundredths of a radian of each other on rx and
   *      rz because those angles describe the eye-to-weapon geometry rather
   *      than the gun, so they are carried over, not re-invented.
   *   3. muzzle crown inside x 1050-1300, y 620-780 at 1920x1080.
   *   4. optic below and right of screen centre.
   *   5. magazine and pistol grip in the lower-right frame.
   *
   * z = -0.31 is this weapon's own number and it is solved, not picked. It
   * sits between the carbine's -0.30 (0.75 m gun) and the LMG's -0.32
   * (0.95 m): this gun is 0.88 m crown-to-buttplate, so it needs more
   * distance than the M4 to keep the muzzle on screen, but LESS than its
   * length alone suggests, because the framing constraint is vertical bulk
   * and a side-mounted reflex over a rocked-forward magazine spans 261 mm
   * from window to floorplate against the M4A1's 291 mm from ocular to
   * floorplate.
   *
   * Projected at a 60 deg vertical FOV (half-height 0.5774|z|, half-width
   * 1.0265|z|), which is the check rifle.js's comment runs:
   *   muzzle -> (1051, 694)   front sight -> (1075, 677)
   *   reflex window -> (1454, 722)   magwell mouth -> (1252, 854)
   *   magazine floorplate -> (1139, 1192)   support wrist -> (976, 741)
   * i.e. the muzzle is 400 px up-LEFT of the optic and heading for the middle
   * of the frame, the two iron sights line up along that same run, and the
   * magazine hangs into the lower right. The floorplate falls 112 px below the
   * frame against the M4A1's 365 px: because this magazine rocks forward as
   * well as down, this weapon loses far less of it off the bottom of the
   * screen than the starter carbine does.
   *
   * The buttplate lands 18 mm BEHIND the eye, so it is culled rather than
   * clipped, and the stock between it and the near plane is 145 mm off axis at
   * a half-width of 51 mm — outside the frustum laterally, the same condition
   * rifle.js documents for its own butt pad and sniper.js inherits.
   *
   * NOT VISUALLY VERIFIED: no capture was run this session (tools/shoot.mjs
   * needs Playwright, which this box cannot run). Every number above is a
   * projection of the model's own measured geometry, not a measurement off a
   * frame.
   */
  hipPos: [0.118, -0.185, -0.31],
  hipRot: [-0.05, 0.08, -0.135],
  adsCant: [0, 0, 0.004],
  /* Eye to the sight window.
   *
   * An OPEN REFLEX, so the aperture budget that governs defs/rifle.js's
   * 0.115 does not apply: there is no tube wall to vignette the picture and
   * no objective bore to stop it down, which is the whole point of putting
   * this kind of sight on this weapon. What sets the number instead is how
   * much of the 38 x 29 mm housing the window fills at the eye. At 0.105 the
   * 31 x 21 mm clear pane subtends 276 x 186 px at 1920x1080 — a frame about
   * as wide as the M4A1's 336 px tube housing but less than half as tall, so
   * the ADS read is a letterbox of open sky with the gun under it rather than
   * a ring of dark tube. Closer than ~0.09 and the mount arm climbs into the
   * frame; further than ~0.12 and the window stops being the dominant shape.
   */
  eyeRelief: 0.105,
  /* Sprint and low-ready carry the same delta off the hip pose that
   * rifle.js uses, so the blend does not translate the weapon sideways on the
   * way into a sprint; the rotations are the shared values every two-handed
   * gun in the game uses, because they describe how the RIG carries a slung
   * weapon rather than the weapon itself. */
  sprintPos: [0.09, -0.262, -0.285],
  sprintRot: [-0.4, 0.6, 0.2],
  lowReadyPos: [0.112, -0.28, -0.299],
  lowReadyRot: [-0.46, 0.125, -0.09],
  /* Heavier than the M4A1 (3.1 kg loaded vs 3.4 kg for this gun) but carried
   * the same way, so sway is up a shade and bob is unchanged. */
  swayScale: 1.06,
  bobScale: 1,
  // Matches models/ak.js's magSize.len exactly.
  magLen: 0.175,
};

export const loadModel = () => import('../models/ak.js').then((m) => m.buildAk);
